import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Filter, X, Eye, ShieldCheck, ShieldAlert } from "lucide-react";

const STATUS_COLORS = {
  success: "bg-green-100 text-green-800 border-green-300",
  failure: "bg-red-100 text-red-800 border-red-300",
};

export function FilterCard({ filters, actions, labels, onFilterChange, onClear }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Filter className="w-4 h-4" />Filtros
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-xs">Ação</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={filters.action}
              onChange={(e) => onFilterChange("action", e.target.value)}
              data-testid="filter-action"
            >
              <option value="">Todas</option>
              {actions.map((a) => <option key={a} value={a}>{labels[a] || a}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Status</Label>
            <select
              className="w-full border rounded px-3 py-2 text-sm mt-1"
              value={filters.status}
              onChange={(e) => onFilterChange("status", e.target.value)}
              data-testid="filter-status"
            >
              <option value="">Ambos</option>
              <option value="success">Sucesso</option>
              <option value="failure">Falha</option>
            </select>
          </div>
          <div>
            <Label className="text-xs">De</Label>
            <Input
              type="date"
              value={filters.date_from}
              onChange={(e) => onFilterChange("date_from", e.target.value)}
              data-testid="filter-from"
            />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input
              type="date"
              value={filters.date_to ? filters.date_to.slice(0, 10) : ""}
              onChange={(e) => onFilterChange("date_to", e.target.value ? e.target.value + "T23:59:59" : "")}
              data-testid="filter-to"
            />
          </div>
          <Button variant="outline" onClick={onClear} data-testid="btn-clear-filters">
            <X className="w-4 h-4 mr-2" />Limpar filtros
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function statusLabel(s) {
  if (s === "success") return "Sucesso";
  if (s === "failure") return "Falha";
  return s;
}

function AuditRow({ it, idx, labels, onOpenDetail }) {
  return (
    <tr key={it.id || `${it.timestamp}-${idx}`} className="border-b hover:bg-slate-50" data-testid={`audit-row-${idx}`}>
      <td className="p-3 text-xs font-mono whitespace-nowrap">
        {new Date(it.timestamp).toLocaleString("pt-BR")}
      </td>
      <td className="p-3">
        <span className="font-medium">{labels[it.action] || it.action}</span>
        <div className="text-xs text-slate-400 font-mono">{it.action}</div>
      </td>
      <td className="p-3">
        <Badge className={STATUS_COLORS[it.status] || "bg-slate-100 text-slate-800"}>
          {statusLabel(it.status)}
        </Badge>
      </td>
      <td className="p-3">
        {it.actor_nome ? (
          <>
            <div className="font-medium">{it.actor_nome}</div>
            <div className="text-xs text-slate-500">{it.actor_role} · {it.actor_cpf}</div>
          </>
        ) : (
          <span className="text-slate-400 italic">anônimo</span>
        )}
      </td>
      <td className="p-3">
        {it.resource_type && (
          <>
            <div className="text-xs font-mono">{it.resource_type}</div>
            <div className="text-xs text-slate-400 font-mono truncate max-w-[160px]">
              {it.resource_id}
            </div>
          </>
        )}
      </td>
      <td className="p-3 text-xs font-mono">{it.ip}</td>
      <td className="p-3 text-center">
        <Button size="sm" variant="ghost" onClick={() => onOpenDetail(it)} data-testid={`btn-detalhe-${idx}`}>
          <Eye className="w-4 h-4" />
        </Button>
      </td>
    </tr>
  );
}

function TableBody({ items, loading, labels, onOpenDetail }) {
  if (loading) {
    return (
      <tr><td colSpan={7} className="p-8 text-center text-slate-500">Carregando...</td></tr>
    );
  }
  if (items.length === 0) {
    return (
      <tr><td colSpan={7} className="p-8 text-center text-slate-500">
        Nenhum registro encontrado com os filtros aplicados
      </td></tr>
    );
  }
  return items.map((it, idx) => (
    <AuditRow key={it.id || `${it.timestamp}-${idx}`} it={it} idx={idx} labels={labels} onOpenDetail={onOpenDetail} />
  ));
}

export function AuditTable({ items, loading, labels, page, totalPages, total, onPrev, onNext, onOpenDetail }) {
  return (
    <Card>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="text-left p-3 font-semibold">Data/Hora</th>
                <th className="text-left p-3 font-semibold">Ação</th>
                <th className="text-left p-3 font-semibold">Status</th>
                <th className="text-left p-3 font-semibold">Usuário</th>
                <th className="text-left p-3 font-semibold">Recurso</th>
                <th className="text-left p-3 font-semibold">IP</th>
                <th className="text-center p-3 font-semibold">Detalhes</th>
              </tr>
            </thead>
            <tbody>
              <TableBody items={items} loading={loading} labels={labels} onOpenDetail={onOpenDetail} />
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t bg-slate-50">
            <div className="text-xs text-slate-600">
              Página {page + 1} de {totalPages} · {total.toLocaleString("pt-BR")} registros
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page === 0} onClick={onPrev} data-testid="btn-prev-page">Anterior</Button>
              <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={onNext} data-testid="btn-next-page">Próxima</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function DetalheDialog({ detail, labels, onClose }) {
  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="dialog-detalhe-audit">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {detail?.status === "success" ? (
              <ShieldCheck className="w-5 h-5 text-green-600" />
            ) : (
              <ShieldAlert className="w-5 h-5 text-red-600" />
            )}
            {detail && (labels[detail.action] || detail.action)}
          </DialogTitle>
          <DialogDescription>
            {detail?.timestamp && new Date(detail.timestamp).toLocaleString("pt-BR")}
          </DialogDescription>
        </DialogHeader>
        {detail && (
          <div className="space-y-2 text-sm">
            <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2">
              <span className="text-slate-500">Ação:</span>
              <span className="font-mono">{detail.action}</span>
              <span className="text-slate-500">Status:</span>
              <span><Badge className={STATUS_COLORS[detail.status]}>{detail.status}</Badge></span>
              <span className="text-slate-500">Usuário:</span>
              <span>{detail.actor_nome || "anônimo"} {detail.actor_role && `(${detail.actor_role})`}</span>
              <span className="text-slate-500">CPF:</span>
              <span className="font-mono">{detail.actor_cpf || "—"}</span>
              <span className="text-slate-500">IP:</span>
              <span className="font-mono">{detail.ip || "—"}</span>
              <span className="text-slate-500">User-Agent:</span>
              <span className="text-xs truncate">{detail.user_agent || "—"}</span>
              {detail.resource_type && (
                <>
                  <span className="text-slate-500">Recurso:</span>
                  <span className="font-mono text-xs">{detail.resource_type} / {detail.resource_id}</span>
                </>
              )}
            </div>
            {detail.meta && Object.keys(detail.meta).length > 0 && (
              <div>
                <div className="text-slate-500 text-xs mt-3 mb-1">Metadados:</div>
                <pre className="bg-slate-50 border rounded p-3 text-xs overflow-auto max-h-[300px] whitespace-pre-wrap break-all">
{JSON.stringify(detail.meta, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
